import React, { useState, useEffect } from 'react';
import {
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Switch,
  FormControl,
  FormLabel,
  Flex,
  Text,
} from '@chakra-ui/react';
import { IKanbanCampoDefinicao } from '../../hooks/useKanban';
import { KanbanDatePicker } from './KanbanDatePicker';

const CurrencyInput: React.FC<{
  value: number | null;
  // eslint-disable-next-line no-unused-vars
  onChange: (v: number | null) => void;
  // eslint-disable-next-line no-unused-vars
  formatDisplay: (v: number | null) => string;
  size?: 'sm' | 'md';
  placeholder?: string;
}> = ({ value, onChange, formatDisplay, size = 'sm', placeholder }) => {
  const [raw, setRaw] = useState('');
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setRaw(formatDisplay(value));
    }
  }, [value, focused]);

  const handleFocus = () => {
    setFocused(true);
    // Mostrar numero limpo ao focar
    if (value !== null && value !== undefined) {
      setRaw(String(value).replace('.', ','));
    } else {
      setRaw('');
    }
  };

  const handleBlur = () => {
    setFocused(false);
    const cleaned = raw.replace(/\./g, '').replace(',', '.');
    const num = parseFloat(cleaned);
    onChange(Number.isNaN(num) ? null : Math.round(num * 100) / 100);
  };

  return (
    <Input
      size={size}
      value={raw}
      onChange={e => setRaw(e.target.value)}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      borderRadius="lg"
      fontSize="13px"
    />
  );
};

interface KanbanCustomFieldInputProps {
  campo: IKanbanCampoDefinicao;
  value: any;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: any) => void;
  size?: 'sm' | 'md';
  isFilter?: boolean;
}

export const KanbanCustomFieldInput: React.FC<KanbanCustomFieldInputProps> = ({
  campo,
  value,
  onChange,
  size = 'sm',
  isFilter = false,
}) => {
  const label = campo.nome;
  const placeholder = isFilter ? `Filtrar por ${campo.nome.toLowerCase()}...` : campo.nome;

  switch (campo.tipo) {
    case 'texto':
      return (
        <FormControl>
          <FormLabel fontSize="12px" color="gray.500" fontWeight="500" mb={1}>
            {label}
            {campo.obrigatorio && !isFilter && (
              <Text as="span" color="red.400" ml={0.5}>
                *
              </Text>
            )}
          </FormLabel>
          <Input
            size={size}
            value={value || ''}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder}
            borderRadius="lg"
            fontSize="13px"
          />
        </FormControl>
      );

    case 'numero':
      return (
        <FormControl>
          <FormLabel fontSize="12px" color="gray.500" fontWeight="500" mb={1}>
            {label}
            {campo.obrigatorio && !isFilter && (
              <Text as="span" color="red.400" ml={0.5}>
                *
              </Text>
            )}
          </FormLabel>
          <NumberInput
            size={size}
            value={value ?? ''}
            onChange={(_, val) => onChange(Number.isNaN(val) ? null : val)}
          >
            <NumberInputField placeholder={placeholder} borderRadius="lg" fontSize="13px" />
          </NumberInput>
        </FormControl>
      );

    case 'moeda': {
      const formatDisplay = (v: number | null | undefined): string => {
        if (v === null || v === undefined) return '';
        return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      };

      return (
        <FormControl>
          <FormLabel fontSize="12px" color="gray.500" fontWeight="500" mb={1}>
            {label}
            {campo.obrigatorio && !isFilter && (
              <Text as="span" color="red.400" ml={0.5}>
                *
              </Text>
            )}
          </FormLabel>
          <CurrencyInput
            value={value}
            onChange={onChange}
            size={size}
            placeholder="R$ 0,00"
            formatDisplay={formatDisplay}
          />
        </FormControl>
      );
    }

    case 'data':
      return (
        <FormControl>
          <FormLabel fontSize="12px" color="gray.500" fontWeight="500" mb={1}>
            {label}
            {campo.obrigatorio && !isFilter && (
              <Text as="span" color="red.400" ml={0.5}>
                *
              </Text>
            )}
          </FormLabel>
          <KanbanDatePicker value={value || ''} onChange={onChange} />
        </FormControl>
      );

    case 'select':
      return (
        <FormControl>
          <FormLabel fontSize="12px" color="gray.500" fontWeight="500" mb={1}>
            {label}
            {campo.obrigatorio && !isFilter && (
              <Text as="span" color="red.400" ml={0.5}>
                *
              </Text>
            )}
          </FormLabel>
          <Select
            size={size}
            value={value || ''}
            onChange={e => onChange(e.target.value)}
            placeholder={isFilter ? 'Todos' : `Selecione ${campo.nome.toLowerCase()}`}
            borderRadius="lg"
            fontSize="13px"
          >
            {(campo.opcoes || []).map(opcao => (
              <option key={opcao} value={opcao}>
                {opcao}
              </option>
            ))}
          </Select>
        </FormControl>
      );

    case 'checkbox':
      if (isFilter) {
        return (
          <FormControl>
            <FormLabel fontSize="12px" color="gray.500" fontWeight="500" mb={1}>
              {label}
            </FormLabel>
            <Select
              size={size}
              value={value === null || value === undefined ? '' : String(value)}
              onChange={e => {
                const v = e.target.value;
                onChange(v === '' ? null : v === 'true');
              }}
              placeholder="Todos"
              borderRadius="lg"
              fontSize="13px"
            >
              <option value="true">Sim</option>
              <option value="false">Nao</option>
            </Select>
          </FormControl>
        );
      }
      return (
        <FormControl>
          <Flex alignItems="center" gap={3} py={1}>
            <Switch
              size={size}
              isChecked={!!value}
              onChange={e => onChange(e.target.checked)}
              colorScheme="blue"
            />
            <Text fontSize="13px" color="gray.600" fontWeight="500">
              {label}
              {campo.obrigatorio && (
                <Text as="span" color="red.400" ml={0.5}>
                  *
                </Text>
              )}
            </Text>
          </Flex>
        </FormControl>
      );

    default:
      return null;
  }
};
